import { Input } from "antd";
import React, { useState } from "react";
import styled from "styled-components";
import { generateOrganization, getOrganizationLevels } from "./data";
import COLORS from "common/Colors";
import { ReactComponent as SearchIcon } from "assets/search.svg";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";
import { Card } from "./Card";

export const OrganizationContext = React.createContext();

export function calculateNumberOfReportees(employee) {
  if (!employee || !employee.reportees) return 0;
  return employee.reportees.length;
}


const Organization = ({ type, team = false }) => {
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);

  const filteruser = userList?.filter(
    (item) => item.userid === userInfo?.userid
  );

  const userListData =
    userList &&
    userList
      ?.filter((user) => user.statusid == "1")
      ?.map((user) => {
        return {
          id: user.employeeid,
          OfficeSupervisorId: user.OfficeSupervisorId,
          fullname: user.fullname,
          profileurl: user.profileurl,
          grade: user.grade,
          RegionalSupervisorId: user.RegionalSupervisorId,
          userid: user.userid,
          designation: user.designation,
          leveldepartmentname: user.leveldepartmentname,
        };
      });

  function getDirectReporteesCount(employeeId) {
    return userListData.filter(
      (u) =>
        (type === "office"
          ? u.OfficeSupervisorId === employeeId
          : u.RegionalSupervisorId === employeeId)
    ).length;
  }


  const loggedInEmployee = userListData.find(
    (u) => u.userid === userInfo?.userid
  );

  const supervisor =
    type === "office"
      ? userListData.find(
          (u) => u.id === loggedInEmployee?.OfficeSupervisorId
        )
      : userListData.find(
          (u) => u.id === loggedInEmployee?.RegionalSupervisorId
        );

  const directReportees = userListData.filter((u) =>
    type === "office"
      ? u.OfficeSupervisorId === loggedInEmployee?.id
      : u.RegionalSupervisorId === loggedInEmployee?.id
  );

  function getSubordinates(data, supervisorId) {
    let result = [];
    let visited = new Set();

    function findSubordinates(supervisorId) {
      if (visited.has(supervisorId)) {
        return;
      }
      visited.add(supervisorId);

      data.forEach((employee) => {
        if (employee.OfficeSupervisorId === supervisorId && type === "office") {
          result.push(employee.id);
          findSubordinates(employee.id);
        }
        if (employee.RegionalSupervisorId === supervisorId && type === "regional") {
          result.push(employee.id);
          findSubordinates(employee.id);
        }
      });
    }

    result.push(supervisorId);
    findSubordinates(supervisorId);
    return result;
  }

  const supervisorId = filteruser[0]?.employeeid;
  const subordinates = getSubordinates(userListData, supervisorId);

  const employeeOrganization = generateOrganization(userListData, type);

  const peopleInLevel = (people) => {
    return people.map((personId) => {
      const person = findEmployeeById(personId);
      const numberOfReportees = calculateNumberOfReportees(person);
      return {
        ...person,
        numberOfReportees,
      };
    });
  };

  function findEmployeeById(id) {
    return employeeOrganization.find((emp) => emp.id === id);
  }

  let organizationLevels = getOrganizationLevels(employeeOrganization);

  const initialOrganization = organizationLevels.map((level) => {
    let levelPeople = peopleInLevel(level.people);
    
    // ✅ For MY TEAM: Reorder to put logged-in user first in their level
    if (team && loggedInEmployee) {
      const loggedInUserIndex = levelPeople.findIndex(p => p.id === loggedInEmployee.id);
      if (loggedInUserIndex > 0) {
        // Move logged-in user to first position
        const loggedInUserData = levelPeople.splice(loggedInUserIndex, 1)[0];
        levelPeople.unshift(loggedInUserData);
      }
    }
    
    return {
      levelName: level.name,
      levelid: level.levelid,
      people: levelPeople,
      show: true,
    };
  });

  const [organization, setOrganization] = useState(initialOrganization);

  let initialActivePerson = [];

	if (team) {

	const loggedInLevelIndex = organization.findIndex(level =>
		level.people.some(p => p.id === loggedInEmployee?.id)
	);

	initialActivePerson = organization.map((level, index) => {

		const loggedInUserInLevel =
		level.people.find(p => p.id === loggedInEmployee?.id);

		if (loggedInUserInLevel) {
		return {
			levelid: level.levelid,
			activePerson: loggedInUserInLevel,
			people: level.people,
		};
		}

		if (index < loggedInLevelIndex) {
		return {
			levelid: level.levelid,
			activePerson: level.people[0] || null,
			people: level.people,
		};
		}

		return {
		levelid: level.levelid,
		activePerson: null,
		people: level.people,
		};
	});

	} else {
	// ✅ IMPORTANT: People Menu default initialization
	initialActivePerson = organization.map(level => ({
		levelid: level.levelid,
		activePerson: level.people[0] || null,
		people: level.people,
	}));
	}

	const [activePerson, setActivePerson] = useState(initialActivePerson);


  return (
    <OrganizationContext.Provider
      value={{
        activePerson,
        setActivePerson,
        organizationLevels,
        organization,
        findEmployeeById,
        setOrganization,
        loggedInEmployee,
        userListData,
        type,
      }}
    >
      <OrganizationWrap>
        <div className="organization">
          {Array.isArray(organization) && organization.map((level, levelIndex) => {
            if (!level?.people?.length) return null;
            
            return (
              <div key={level?.levelid} className={`level ${levelIndex + 1}`}>
                <div className="level-box">{level?.levelName}</div>
                  <div className="people">
                    {level?.people.map((person) => {
                      const isMyTeamView = team === true;

                      if (isMyTeamView) {

						const isLoggedInUser =
							person.id === loggedInEmployee?.id;

						const isSubordinate =
							subordinates.includes(person.id) &&
							person.id !== loggedInEmployee?.id;

						let shouldHideBadge = !isSubordinate;

						if (isLoggedInUser) {
							person.numberOfReportees =
							getDirectReporteesCount(person.id);
							shouldHideBadge = false;
						}

						// 🔥 SHOW FULL LEVELS ABOVE LOGGED IN USER
						const loggedInLevelIndex = organization.findIndex(level =>
							level.people.some(p => p.id === loggedInEmployee?.id)
						);

						if (levelIndex <= loggedInLevelIndex) {
							return (
							<React.Fragment key={person.id}>
								<Card
								linked={
									activePerson[levelIndex]?.activePerson?.id === person.id
								}
								person={person}
								subordinates={subordinates}
								team={team}
								type={type}
								filteruser={filteruser}
								isAboveLoggedInUser={!isLoggedInUser && !isSubordinate}
								isSideTeammate={false}
								hideCountBadge={shouldHideBadge}
								isLoggedInUser={isLoggedInUser}
								/>
							</React.Fragment>
							);
						}

						// 🔥 BELOW LOGGED IN USER → TREE FILTERING
						const previousLevelActivePersonId =
							activePerson[levelIndex - 1]?.activePerson?.id;

						if (
							previousLevelActivePersonId &&
							person.reportsTo === previousLevelActivePersonId
						) {
							return (
							<React.Fragment key={person.id}>
								<Card
								person={person}
								linked={
									activePerson[levelIndex]?.activePerson?.id === person.id
								}
								subordinates={subordinates}
								team={team}
								type={type}
								filteruser={filteruser}
								isAboveLoggedInUser={!isLoggedInUser && !isSubordinate}
								isSideTeammate={false}
								hideCountBadge={shouldHideBadge}
								isLoggedInUser={isLoggedInUser}
								/>
							</React.Fragment>
							);
						}

						return null;
						}


                      // ✅ PEOPLE MENU - NO CHANGES
                      const rootPerson =
                        activePerson[levelIndex]?.activePerson?.reportsTo === "none";
                      if (rootPerson) {
                        return (
                          <React.Fragment key={person.id}>
                            <Card
                              linked={
                                activePerson[levelIndex]?.activePerson?.id === person.id
                              }
                              person={person}
                              subordinates={subordinates}
                              team={team}
                              type={type}
                              filteruser={filteruser}
                            />
                          </React.Fragment>
                        );
                      }
                      const previousLevelActivePersonId =
                        activePerson[levelIndex - 1]?.activePerson?.id;

                      if (
                        previousLevelActivePersonId &&
                        person.reportsTo === previousLevelActivePersonId
                      ) {
                        return (
                          <React.Fragment key={person.id}>
                            <Card
                              person={person}
                              linked={
                                activePerson[levelIndex]?.activePerson?.id ===
                                person.id
                              }
                              subordinates={subordinates}
                              team={team}
                              type={type}
                              filteruser={filteruser}
                            />
                          </React.Fragment>
                        );
                      }
                      return null;
                    })}
                  </div>
              </div>
            );
          })}
        </div>
      </OrganizationWrap>
    </OrganizationContext.Provider>
  );
};

export default Organization;

const OrganizationWrap = styled.div`
  .organization {
    margin-top: 1rem;

    .level {
      display: flex;
      justify-content: start;
      align-items: start;
      gap: 1rem;

      .level-box {
        flex-shrink: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 28px;
        height: 28px;
        color: white;
        background-color: ${COLORS.BLUE};
        border-radius: 5px;
        position: relative;
        cursor: pointer;
      }

      .people {
        display: grid;
        grid-auto-flow: column;
        gap: 1rem;
      }
    }
  }
`;